import { motion } from "motion/react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Compass, MessageCircle, RotateCcw } from "lucide-react";
import { ReactNode } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  RIASEC_TYPES,
  RIASEC_FIELDS_MAP,
  RiasecCode,
} from "../../Data/mockDataCareerTest";

function LockedResultSection({
  locked,
  children,
}: {
  locked: boolean;
  children: ReactNode;
}) {
  if (!locked) return <>{children}</>;

  return (
    <div className="relative">
      <div className="blur-sm select-none pointer-events-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/45 backdrop-blur-[1px]">
        <div className="flex flex-col rounded-xl bg-white/95 border border-violet-100 px-4 py-2 text-center shadow-sm">
          <p
            className="text-[12px] text-violet-700"
            style={{ fontWeight: 700 }}
          >
            Sign up to view your full recommendations
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ResultsScreen({
  scores,
  topTypes,
  onRetake,
  onSave,
  saved,
}: {
  scores: Record<RiasecCode, number>;
  topTypes: RiasecCode[];
  onRetake: () => void;
  onSave: () => void;
  saved: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLocked = !user;
  const primaryType = RIASEC_TYPES.find((t) => t.code === topTypes[0])!;
  const topTypeMeta = topTypes.map(
    (code) => RIASEC_TYPES.find((t) => t.code === code)!,
  );

  // Deduplicated fields from top types
  const allFields = [
    ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].fields)),
  ].slice(0, 8);
  const allCourses = [
    ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].courses)),
  ].slice(0, 6);
  const allCareers = [
    ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].careers)),
  ].slice(0, 6);

  const codeLabel = topTypes.join("");

  if (isLocked) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl p-8 text-white text-center mb-6">
          <p
            className="text-[13px] text-white/75 uppercase tracking-widest mb-2"
            style={{ fontWeight: 700 }}
          >
            Your career profile is ready
          </p>
          <h2
            className="text-[30px] leading-tight mb-3"
            style={{ fontWeight: 900 }}
          >
            We found your top 3 career personality patterns
          </h2>
          <p
            className="text-[14px] text-white/80 max-w-md mx-auto"
            style={{ fontWeight: 400 }}
          >
            Create your student profile to unlock your RIASEC code, matching
            study fields, example courses, and career paths.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          {topTypes.map((type, index) => (
            <div
              key={type}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-center"
            >
              <p
                className="text-[11px] text-slate-400 uppercase tracking-widest mb-2"
                style={{ fontWeight: 700 }}
              >
                Match {index + 1}
              </p>
              <div className="h-12 rounded-xl bg-slate-100 blur-sm" />
            </div>
          ))}
        </div>

        <div className="bg-violet-50 border border-violet-100 rounded-2xl p-5 mb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p
              className="text-[14px] text-violet-900"
              style={{ fontWeight: 800 }}
            >
              Unlock your full result in the student portal
            </p>
            <p
              className="text-[12px] text-violet-600"
              style={{ fontWeight: 400 }}
            >
              Your test has been saved temporarily for this browser session.
            </p>
          </div>
          <Link
            to="/register"
            className="flex-shrink-0 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-[13px] hover:bg-violet-700 transition-colors"
            style={{ fontWeight: 700 }}
          >
            Create Profile
          </Link>
        </div>

        <button
          onClick={onRetake}
          className="flex items-center gap-1.5 mx-auto mt-4 text-[13px] text-slate-400 hover:text-slate-600 transition-colors"
          style={{ fontWeight: 500 }}
        >
          <RotateCcw className="w-3.5 h-3.5" /> Retake the test
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      {/* Hero banner */}
      <div
        className={`bg-gradient-to-br ${primaryType.bg} rounded-3xl p-8 text-white text-center mb-6 relative overflow-hidden`}
      >
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/10 -translate-y-1/3 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/4" />
        <div className="relative z-10">
          <p
            className="text-[13px] text-white/70 uppercase tracking-widest mb-2"
            style={{ fontWeight: 700 }}
          >
            Your Career Code
          </p>
          <p
            className="text-[56px] leading-none mb-2"
            style={{ fontWeight: 900 }}
          >
            {codeLabel}
          </p>
          <p
            className="text-[18px] text-white/90 mb-1"
            style={{ fontWeight: 700 }}
          >
            {topTypeMeta.map((t) => t.label).join(" · ")}
          </p>
          <p className="text-[14px] text-white/70" style={{ fontWeight: 400 }}>
            {topTypeMeta.map((t) => t.nickname).join(" · ")}
          </p>
        </div>
      </div>

      {/* Top type cards */}
      <div className="flex flex-col gap-4 mb-6">
        {topTypeMeta.map((type, i) => {
          const score = scores[type.code];
          const pct = Math.round((score / 10) * 100);
          return (
            <motion.div
              key={type.code}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-[20px] flex-shrink-0"
                  style={{ backgroundColor: `${type.color}18` }}
                >
                  {type.emoji}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p
                      className="text-[14px] text-slate-900"
                      style={{ fontWeight: 700 }}
                    >
                      {type.label}
                    </p>
                    <span
                      className="text-[11px] px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: type.color, fontWeight: 700 }}
                    >
                      {type.nickname}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: type.color }}
                      />
                    </div>
                    <span
                      className="text-[11px] text-slate-500 flex-shrink-0"
                      style={{ fontWeight: 600 }}
                    >
                      {score}/10
                    </span>
                  </div>
                </div>
              </div>
              <p
                className="text-[13px] text-slate-500 leading-relaxed"
                style={{ fontWeight: 400 }}
              >
                {type.description}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Fields of study */}
      <LockedResultSection locked={isLocked}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-4"
        >
          <h3
            className="text-[14px] text-slate-900 mb-3"
            style={{ fontWeight: 700 }}
          >
            🎓 Recommended Fields of Study
          </h3>
          <div className="flex flex-wrap gap-2">
            {allFields.map((field, i) => (
              <span
                key={i}
                className="px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100 text-[12px] text-violet-700"
                style={{ fontWeight: 600 }}
              >
                {field}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Courses + Careers */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="grid sm:grid-cols-2 gap-4 mb-6"
        >
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h3
              className="text-[14px] text-slate-900 mb-3"
              style={{ fontWeight: 700 }}
            >
              📚 Example Courses
            </h3>
            <div className="flex flex-col gap-2">
              {allCourses.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                  <span
                    className="text-[12px] text-slate-600"
                    style={{ fontWeight: 400 }}
                  >
                    {c}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h3
              className="text-[14px] text-slate-900 mb-3"
              style={{ fontWeight: 700 }}
            >
              💼 Career Paths
            </h3>
            <div className="flex flex-col gap-2">
              {allCareers.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <span
                    className="text-[12px] text-slate-600"
                    style={{ fontWeight: 400 }}
                  >
                    {c}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </LockedResultSection>

      {/* Save to profile */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-violet-50 border border-violet-100 rounded-2xl p-4 mb-4 flex items-center justify-between gap-4"
      >
        <div>
          <p
            className="text-[13px] text-violet-900"
            style={{ fontWeight: 700 }}
          >
            {user
              ? "Save results to your profile"
              : "Create your student profile"}
          </p>
          <p
            className="text-[12px] text-violet-600"
            style={{ fontWeight: 400 }}
          >
            {user
              ? "Your counsellor can use this to personalise your university recommendations."
              : "Sign up to save your results and unlock your full recommendations."}
          </p>
        </div>
        {!user ? (
          <Link
            to="/register"
            className="flex-shrink-0 px-4 py-2 rounded-xl bg-violet-600 text-white text-[13px] hover:bg-violet-700 transition-colors"
            style={{ fontWeight: 700 }}
          >
            Sign Up
          </Link>
        ) : saved ? (
          <div
            className="flex items-center gap-1.5 text-green-600 flex-shrink-0 text-[13px]"
            style={{ fontWeight: 700 }}
          >
            <CheckCircle2 className="w-4 h-4" /> Saved!
          </div>
        ) : (
          <button
            onClick={onSave}
            className="flex-shrink-0 px-4 py-2 rounded-xl bg-violet-600 text-white text-[13px] hover:bg-violet-700 transition-colors"
            style={{ fontWeight: 700 }}
          >
            Save
          </button>
        )}
      </motion.div>

      {/* CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <button
          onClick={() => navigate("/course-finder")}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] shadow-md hover:-translate-y-0.5 transition-all"
          style={{ fontWeight: 700 }}
        >
          <Compass className="w-4 h-4" /> Explore Course Finder
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl border border-slate-200 text-slate-700 text-[14px] hover:bg-slate-50 transition-all"
          style={{ fontWeight: 600 }}
        >
          <MessageCircle className="w-4 h-4" /> Talk to My Counsellor
        </button>
      </motion.div>

      <button
        onClick={onRetake}
        className="flex items-center gap-1.5 mx-auto mt-4 text-[13px] text-slate-400 hover:text-slate-600 transition-colors"
        style={{ fontWeight: 500 }}
      >
        <RotateCcw className="w-3.5 h-3.5" /> Retake the test
      </button>
    </motion.div>
  );
}
